import { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';

interface ServiceStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'down' | 'checking';
  latency?: number;
  message?: string;
}

interface EndpointHealth {
  endpoint: string;
  method: string;
  status: number | null;
  latency: number | null;
  error?: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://republic-atlas-api-946143380554.us-central1.run.app/api/v1';

const STATUS_CONFIG = {
  healthy: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Healthy' },
  degraded: { color: '#eab308', bg: 'rgba(234,179,8,0.1)', label: 'Degraded' },
  down: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Down' },
  checking: { color: '#6366f1', bg: 'rgba(99,102,241,0.1)', label: 'Checking...' },
};

export const ApiStatus = () => {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [endpoints, setEndpoints] = useState<EndpointHealth[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const getAuthToken = useCallback(async () => {
    try {
      const user = getAuth().currentUser;
      if (user) return await user.getIdToken(true);
    } catch {
      // Ignore
    }
    return null;
  }, []);

  const checkEndpoint = async (endpoint: string, method: string = 'GET'): Promise<EndpointHealth> => {
    const start = performance.now();
    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE}${endpoint}`, { method, headers });
      return {
        endpoint,
        method,
        status: response.status,
        latency: Math.round(performance.now() - start),
      };
    } catch (e) {
      return {
        endpoint,
        method,
        status: null,
        latency: Math.round(performance.now() - start),
        error: String(e),
      };
    }
  };

  const runHealthCheck = useCallback(async () => {
    setIsChecking(true);
    
    // Initialize services as checking
    setServices([
      { name: 'API Core', status: 'checking' },
      { name: 'Database', status: 'checking' },
      { name: 'Stripe', status: 'checking' },
      { name: 'OpenFEC', status: 'checking' },
      { name: 'Perplexity', status: 'checking' },
    ]);
    
    const endpointsToCheck = [
      { endpoint: '/health', method: 'GET' },
      { endpoint: '/offices?limit=1', method: 'GET' },
      { endpoint: '/candidates?limit=1', method: 'GET' },
      { endpoint: '/subscriptions/products', method: 'GET' },
      { endpoint: '/perplexity/status', method: 'GET' },
      { endpoint: '/openfec/status', method: 'GET' },
      { endpoint: '/polymarket/status', method: 'GET' },
    ];

    const results = await Promise.all(
      endpointsToCheck.map(e => checkEndpoint(e.endpoint, e.method))
    );
    
    setEndpoints(results);

    // Derive service status
    const healthCheck = results.find(r => r.endpoint === '/health');
    const dbCheck = results.find(r => r.endpoint === '/offices?limit=1');
    const stripeCheck = results.find(r => r.endpoint === '/subscriptions/products');
    const perplexityCheck = results.find(r => r.endpoint === '/perplexity/status');
    const openfecCheck = results.find(r => r.endpoint === '/openfec/status');

    const deriveStatus = (check?: EndpointHealth): 'healthy' | 'degraded' | 'down' => {
      if (!check) return 'down';
      if (check.status === 200) return 'healthy';
      if (check.status === 404 || check.status === 503) return 'degraded';
      if (check.status && check.status < 500) return 'degraded';
      return 'down';
    };

    setServices([
      { name: 'API Core', status: deriveStatus(healthCheck), latency: healthCheck?.latency ?? undefined },
      { name: 'Database', status: deriveStatus(dbCheck), latency: dbCheck?.latency ?? undefined },
      { name: 'Stripe', status: deriveStatus(stripeCheck), latency: stripeCheck?.latency ?? undefined },
      { name: 'OpenFEC', status: deriveStatus(openfecCheck), latency: openfecCheck?.latency ?? undefined, message: openfecCheck?.status === 503 ? 'Not configured' : undefined },
      { name: 'Perplexity', status: deriveStatus(perplexityCheck), latency: perplexityCheck?.latency ?? undefined, message: perplexityCheck?.status === 503 ? 'Not configured' : undefined },
    ]);

    setLastCheck(new Date());
    setIsChecking(false);
  }, [getAuthToken]);

  // Initial check and auto-refresh
  useEffect(() => {
    runHealthCheck();
    
    if (autoRefresh) {
      const interval = setInterval(runHealthCheck, 30000);
      return () => clearInterval(interval);
    }
  }, [runHealthCheck, autoRefresh]);

  const healthyCount = services.filter(s => s.status === 'healthy').length;
  const totalServices = services.filter(s => s.status !== 'checking').length;
  const overallStatus = healthyCount === totalServices ? 'healthy' : healthyCount > 0 ? 'degraded' : 'down';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Overall Status Card */}
      <div 
        style={{
          padding: 20,
          borderRadius: 12,
          background: STATUS_CONFIG[overallStatus].bg,
          border: `1px solid ${STATUS_CONFIG[overallStatus].color}30`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div 
                style={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  background: STATUS_CONFIG[overallStatus].color,
                  boxShadow: `0 0 12px ${STATUS_CONFIG[overallStatus].color}`,
                  animation: overallStatus !== 'healthy' ? 'pulse 2s infinite' : 'none',
                }} 
              />
              <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>
                {overallStatus === 'healthy' ? 'All Systems Operational' :
                 overallStatus === 'degraded' ? 'Partial Outage' : 'Major Outage'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              {healthyCount}/{totalServices} services healthy
              {lastCheck && ` • Updated ${lastCheck.toLocaleTimeString()}`}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: 'none',
                background: autoRefresh ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.1)',
                color: autoRefresh ? '#22c55e' : 'rgba(255,255,255,0.6)',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {autoRefresh ? '⏸' : '▶'} Auto
            </button>
            <button
              onClick={runHealthCheck}
              disabled={isChecking}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: isChecking ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.2)',
                color: isChecking ? 'rgba(255,255,255,0.4)' : '#6366f1',
                fontSize: 12,
                fontWeight: 500,
                cursor: isChecking ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {isChecking ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    animation: 'spin 1s linear infinite',
                  }}>◐</span>
                  Checking...
                </>
              ) : (
                '↻ Refresh'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Service Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {services.map((service) => {
          const config = STATUS_CONFIG[service.status];
          return (
            <div
              key={service.name}
              style={{
                padding: 16,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div 
                  style={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: '50%', 
                    background: config.color,
                    boxShadow: service.status === 'checking' ? 'none' : `0 0 8px ${config.color}50`,
                    animation: service.status === 'checking' ? 'pulse 1s infinite' : 'none',
                  }} 
                />
                <span style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>
                  {service.name}
                </span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: config.color, fontWeight: 500 }}>
                  {config.label}
                </span>
                {service.latency !== undefined && (
                  <span style={{ 
                    color: service.latency < 200 ? '#22c55e' : service.latency < 500 ? '#eab308' : '#ef4444',
                  }}>
                    {service.latency}ms
                  </span>
                )}
              </div>
              
              {service.message && (
                <div style={{ 
                  fontSize: 11, 
                  color: 'rgba(255,255,255,0.4)', 
                  marginTop: 8,
                  padding: '4px 8px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: 4,
                }}>
                  {service.message}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Endpoint Details */}
      <div 
        style={{
          background: 'rgba(255,255,255,0.02)',
          borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.05)',
          overflow: 'hidden',
        }}
      >
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          fontSize: 12,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.6)',
          letterSpacing: '0.05em',
        }}>
          ENDPOINT HEALTH
        </div>
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {endpoints.map((ep) => (
            <div
              key={ep.endpoint}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 16px',
                borderBottom: '1px solid rgba(255,255,255,0.03)',
                fontSize: 12,
              }}
            >
              <span style={{ 
                fontFamily: 'monospace',
                fontWeight: 600,
                color: ep.method === 'GET' ? '#22c55e' : '#3b82f6',
                width: 40,
              }}>
                {ep.method}
              </span>
              <span style={{ 
                fontFamily: 'monospace',
                fontWeight: 600,
                width: 40,
                color: !ep.status ? '#ef4444' : ep.status < 300 ? '#22c55e' : ep.status < 500 ? '#eab308' : '#ef4444',
              }}>
                {ep.status || 'ERR'}
              </span>
              <span style={{ flex: 1, color: 'rgba(255,255,255,0.6)' }}>
                {ep.endpoint}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
                {ep.latency ? `${ep.latency}ms` : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div style={{ display: 'flex', gap: 12 }}>
        <a
          href="https://console.cloud.google.com/run/detail/us-central1/republic-atlas-api/metrics?project=republic-atlas"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.05)',
            color: '#6366f1',
            fontSize: 12,
            fontWeight: 500,
            textDecoration: 'none',
            textAlign: 'center',
            transition: 'all 0.15s ease',
          }}
        >
          📊 Cloud Run
        </a>
        <a
          href="https://console.cloud.google.com/monitoring/dashboards?project=republic-atlas"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.05)',
            color: '#6366f1',
            fontSize: 12,
            fontWeight: 500,
            textDecoration: 'none',
            textAlign: 'center',
            transition: 'all 0.15s ease',
          }}
        >
          📈 Monitoring
        </a>
        <a
          href="https://console.cloud.google.com/logs?project=republic-atlas"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.05)',
            color: '#6366f1',
            fontSize: 12,
            fontWeight: 500,
            textDecoration: 'none',
            textAlign: 'center',
            transition: 'all 0.15s ease',
          }}
        >
          📜 Logs
        </a>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};
